/*
 * What was ordered, read off `completedOrder` on GET /lab-orders/{id} (KIN-1976).
 *
 * The detail page used to show an order only through Healthlink's own stylesheet — a printout
 * rendered in an iframe. That is the document, not the data, so nothing on the screen could be
 * read, searched or laid out by us. This turns the structured reading the backend now supplies
 * into something the page can render.
 *
 * Display text is what gets shown, never the code: tests are locally coded (`L`) and there is no
 * national catalogue yet (KIN-1500), so the sender's own text is the name. The code goes on the
 * tooltip, where it helps during testing without competing with the name.
 */

export const COMPLETED_ORDER_STATE = {
  notDownloaded: "NOT_DOWNLOADED",
  unreadable: "UNREADABLE",
  ready: "READY",
};

/*
 * Seen on dev: CH 47, HM 6, MB 3, IMM 1, VR 1. Anything else shows as its raw code rather than
 * being hidden — an unmapped section is information, and this list will grow.
 */
const SECTION_LABELS = {
  CH: "Chemistry",
  HM: "Haematology",
  MB: "Microbiology",
  IMM: "Immunology",
  VR: "Virology",
};

const PRIORITY_LABELS = {
  R: "Routine",
  S: "Stat",
  A: "ASAP",
};

const hasText = (value) => typeof value === "string" && !!value.trim();

/** A coded element's display text, falling back to the code so a value never disappears. */
export const codedText = (coded) => {
  if (hasText(coded?.text)) return coded.text.trim();
  if (hasText(coded?.code)) return coded.code.trim();

  return null;
};

export const formatDiagnosticSection = (section) => {
  if (!hasText(section)) return null;

  const key = section.trim().toUpperCase();

  return SECTION_LABELS[key] ?? section.trim();
};

export const formatPriority = (priority) => {
  if (!hasText(priority)) return null;

  const key = priority.trim().toUpperCase();

  return PRIORITY_LABELS[key] ?? priority.trim();
};

/** "Urine", or "Urine — URINE CSU" when the test asks where it was taken from. */
export const formatSpecimen = (test) => {
  const source = codedText(test?.specimenSource);
  const site = codedText(test?.specimenSite);

  if (!source && !site) return null;
  if (!site) return source;
  if (!source) return site;

  return `${source} — ${site}`;
};

/**
 * A question and its answer.
 *
 * `parsed: false` means the backend could not read the answer. It is shown as unreadable with the
 * reason on hover, never as an empty value — a blank that reads as "no answer" is the failure
 * KIN-1974 was built to avoid (KIN-1226).
 */
export const formatQuestion = (question) => {
  const label = codedText(question?.question);

  if (!label) return null;

  if (question?.parsed === false) {
    return {
      label,
      answer: "Answer couldn't be read",
      isUnreadable: true,
      reason: hasText(question?.unparsedReason) ? question.unparsedReason : null,
    };
  }

  const answer = codedText(question?.codedAnswer) ?? (hasText(question?.textAnswer) ? question.textAnswer.trim() : null);

  if (!answer) {
    return {
      label,
      answer: "Answer couldn't be read",
      isUnreadable: true,
      reason: "The order carried no answer for this question.",
    };
  }

  return { label, answer, isUnreadable: false, reason: null };
};

const summariseTest = (test, index) => ({
  key: test?.sequence ?? index,
  sequence: test?.sequence ?? null,
  name: codedText(test?.test) ?? "Unnamed test",
  code: hasText(test?.test?.code) ? test.test.code.trim() : null,
  isUnrecognisedCoding: test?.test?.recognisedCodingSystem === false,
  section: formatDiagnosticSection(test?.diagnosticSection),
  priority: formatPriority(test?.priority),
  requestedAt: hasText(test?.requestedAt) ? test.requestedAt : null,
  specimen: formatSpecimen(test),
  questions: (test?.questions ?? []).map(formatQuestion).filter(Boolean),
});

const bySequence = (left, right) => {
  if (left.sequence == null && right.sequence == null) return 0;
  if (left.sequence == null) return 1;
  if (right.sequence == null) return -1;

  return left.sequence - right.sequence;
};

/**
 * The four states the page has to tell apart.
 *
 * `notDownloaded` keeps the existing "appears here once completed" behaviour. `unreadable` is an
 * order Healthlink returned but the backend could not read — worth saying out loud, because the
 * stylesheet preview below can still show it.
 */
export const summariseCompletedOrder = (order) => {
  if (!order?.downloaded) {
    return { state: COMPLETED_ORDER_STATE.notDownloaded, tests: [], clinicalDetails: null };
  }

  const completed = order.completedOrder;

  if (!completed) {
    return { state: COMPLETED_ORDER_STATE.unreadable, tests: [], clinicalDetails: null };
  }

  return {
    state: COMPLETED_ORDER_STATE.ready,
    // Clinical details belong to the order, not the test: Healthlink repeats them on every OBR
    // and the backend already de-duplicates them.
    clinicalDetails: hasText(completed.clinicalDetails)
      ? completed.clinicalDetails.trim()
      : null,
    tests: (completed.tests ?? []).map(summariseTest).sort(bySequence),
  };
};
